import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Plus, ChevronDown, ChevronLeft, ChevronRight,
  Eye, Edit3, FileText, Loader2, AlertCircle, X, Search
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── constants ───────────── */
const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Drafts', value: 'draft' },
  { label: 'Pending Branch', value: 'pending_branch_approval' },
  { label: 'Returned', value: 'returned' },
  { label: 'Business Review', value: 'submitted' },
  { label: 'GM Review', value: 'pending_gm_review' },
  { label: 'Accounts Review', value: 'pending_accounts_review' },
  { label: 'Processed', value: 'processed' },
];

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_branch_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  returned: 'bg-red-50 text-red-700 border-red-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_gm_review: 'bg-orange-50 text-orange-700 border-orange-200',
  pending_accounts_review: 'bg-purple-50 text-purple-700 border-purple-200',
  processed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_branch_approval: 'Pending Branch Approval',
  returned: 'Returned',
  submitted: 'Pending Business Review',
  pending_gm_review: 'Pending GM Review',
  pending_accounts_review: 'Pending Accounts Review',
  processed: 'Processed',
};

/* ───────────── helpers ───────────── */
const getRoleName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.roles?.[0]?.name || '';
  } catch { return ''; }
};

/** Normalize role string: "Branch Worker" | "Branch-Worker" | "branch_worker" → "branchworker" */
const norm = (role) => role.toLowerCase().replace(/[\s_-]/g, '');

const canCreateIncidental = (role) => {
  const n = norm(role);
  return n === 'admin' || n === 'branchworker';
};

const canEditIncidental = (role, status) => {
  const n = norm(role);
  if (n === 'admin') return ['draft', 'returned'].includes(status);
  if (n === 'branchworker') return ['draft', 'returned'].includes(status);
  return false;
};

/** Return only the status tabs relevant for this role */
const getTabsForRole = (role) => {
  const n = norm(role);
  if (n === 'admin') return STATUS_TABS;
  if (n === 'branchworker')
    return STATUS_TABS.filter(t => ['', 'draft', 'pending_branch_approval', 'returned', 'submitted', 'pending_gm_review', 'pending_accounts_review', 'processed'].includes(t.value));
  if (n === 'branch')
    return STATUS_TABS.filter(t => ['', 'pending_branch_approval', 'submitted', 'pending_gm_review', 'pending_accounts_review', 'processed'].includes(t.value));
  if (n === 'business')
    return STATUS_TABS.filter(t => ['', 'submitted', 'pending_gm_review', 'pending_accounts_review', 'processed'].includes(t.value));
  if (n === 'gm')
    return STATUS_TABS.filter(t => ['', 'pending_gm_review', 'pending_accounts_review', 'processed'].includes(t.value));
  if (n === 'accounts')
    return STATUS_TABS.filter(t => ['', 'pending_accounts_review', 'processed'].includes(t.value));
  return STATUS_TABS; // fallback: show all
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  return `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ───────────── component ───────────── */
export default function StoreIncidentalList() {
  const navigate = useNavigate();

  /* state */
  const [role, setRole] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('');

  /* master data for filters */
  const [seasons, setSeasons] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);

  const [filters, setFilters] = useState({
    season_id: '',
    commodity_id: '',
    state_id: '',
    branch_id: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const fetchRef = useRef(null);

  /* ── load role + master data ── */
  useEffect(() => {
    const userRole = getRoleName();
    setRole(userRole);

    const load = async () => {
      try {
        const promises = [
          axios.get('seasons?paginate=false'),
          axios.get('commodity'),
          axios.get('states'),
        ];
        if (userRole.toLowerCase().replace(/[\s_-]/g, '') === 'admin') {
          promises.push(axios.get('user/branches?paginate=false'));
        }
        const [sRes, cRes, stRes, bRes] = await Promise.all(promises);
        setSeasons(sRes.data?.seasons || sRes.data?.data || (Array.isArray(sRes.data) ? sRes.data : []));
        setCommodities(cRes.data?.commodities || cRes.data?.data || (Array.isArray(cRes.data) ? cRes.data : []));
        setStates(stRes.data?.states || stRes.data?.data || (Array.isArray(stRes.data) ? stRes.data : []));
        if (bRes) {
          setBranches(bRes.data?.data || bRes.data?.branches || (Array.isArray(bRes.data) ? bRes.data : []));
        }
      } catch (err) {
        console.error('Master data load error:', err);
      }
    };
    load();
  }, []);

  /* ── fetch list ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      params.append('paginate', 'true');
      if (activeTab) params.append('status', activeTab);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });

      const res = await axios.get(`store-incidentals?${params.toString()}`);
      const d = res.data;
      setData(d.data || d.storeIncidentals || []);
      const t = d.total || d.meta?.total || 0;
      setTotal(t);
      setTotalPages(Math.ceil(t / limit) || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch records');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, activeTab, filters]);

  useEffect(() => {
    if (fetchRef.current) clearTimeout(fetchRef.current);
    fetchRef.current = setTimeout(fetchData, 200);
    return () => { if (fetchRef.current) clearTimeout(fetchRef.current); };
  }, [fetchData]);

  /* handlers */
  const handleTabChange = (val) => { setActiveTab(val); setPage(1); };
  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters({ season_id: '', commodity_id: '', state_id: '', branch_id: '' });
    setPage(1);
  };

  /* ── render ── */
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="w-full">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4"
        >
          <div>
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Incidental Management</h5>
            <h1 className="text-3xl md:text-4xl font-black text-blue-800 tracking-tight">Store Incidentals</h1>
            <p className="text-slate-500 mt-1 text-sm">View, create, and manage incidental records across the approval workflow.</p>
          </div>
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowFilters(p => !p)}
              className="flex items-center gap-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 px-5 py-3 rounded-2xl font-bold shadow-sm transition-all text-sm"
            >
              <Search size={18} /> Filters
              <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </motion.button>
            {canCreateIncidental(role) && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/incidental/store/create')}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-blue-100 transition-all text-sm"
              >
                <Plus size={20} /> Create New
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* ── Status Tabs ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.05 }}
          className="flex gap-2 mb-6 overflow-x-auto pb-1 no-scrollbar"
        >
          {getTabsForRole(role).map(tab => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`whitespace-nowrap px-5 py-2.5 rounded-2xl text-sm font-bold transition-all border ${activeTab === tab.value
                  ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* ── Filters ── */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Season */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Season</label>
                    <div className="relative">
                      <select name="season_id" value={filters.season_id} onChange={handleFilterChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm">
                        <option value="">All Seasons</option>
                        {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  {/* Commodity */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Commodity</label>
                    <div className="relative">
                      <select name="commodity_id" value={filters.commodity_id} onChange={handleFilterChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm">
                        <option value="">All Commodities</option>
                        {commodities.map(c => <option key={c.id} value={c.id}>{c.commodityname || c.name}</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  {/* State */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">State</label>
                    <div className="relative">
                      <select name="state_id" value={filters.state_id} onChange={handleFilterChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm">
                        <option value="">All States</option>
                        {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  {/* Branch (admin only) */}
                  {norm(role) === 'admin' && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Branch</label>
                      <div className="relative">
                        <select name="branch_id" value={filters.branch_id} onChange={handleFilterChange}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm">
                          <option value="">All Branches</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.first_name || b.name}</option>)}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-4">
                  <button onClick={clearFilters}
                    className="flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors">
                    <X size={15} /> Clear Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Error ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
              <AlertCircle size={20} /> {error}
              <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded-lg transition"><X size={16} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  {['ID', 'Season', 'Commodity', 'State', 'Branch', 'Procurement Qty', 'Total Claimed', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-600">
                        <Loader2 size={32} className="animate-spin" />
                        <span className="text-sm font-bold">Loading records...</span>
                      </div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-slate-400">
                        <FileText size={40} strokeWidth={1.5} />
                        <span className="text-sm font-bold">No records found</span>
                        <p className="text-xs text-slate-400 max-w-xs">Try adjusting your filters or create a new store incidental record.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((item, idx) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => navigate(`/incidental/store/${item.id}`)}
                      className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                    >
                      <td className="px-5 py-4 text-xs font-mono text-slate-400">{item.reference_id || '—'}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-800">{item.season?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-700">{item.commodity?.commodityname || item.commodity?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm text-slate-600">{item.state?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm text-slate-600">{item.branch?.first_name || item.branch?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-slate-800">{item.procurement_quantity ? parseFloat(item.procurement_quantity).toLocaleString('en-IN') : '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-slate-800">{formatCurrency(item.total_claimed_amount)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold border ${STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                      </td>
                      <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/incidental/store/${item.id}`)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          {canEditIncidental(role, item.status) && (
                            <button
                              onClick={() => navigate(`/incidental/store/${item.id}/edit`)}
                              className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.length > 0 && (
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-5 border-t border-slate-100 bg-slate-50/30">
              <div className="text-xs text-slate-500 font-medium">
                Showing <span className="text-blue-600 font-bold">{Math.min(total, (page - 1) * limit + 1)}</span> to{' '}
                <span className="text-blue-600 font-bold">{Math.min(total, page * limit)}</span> of{' '}
                <span className="text-blue-600 font-bold">{total}</span> records
              </div>
              <div className="flex items-center gap-2">
                <button disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition font-bold text-xs">
                  <ChevronLeft size={16} /> Prev
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pn;
                    if (totalPages <= 5) pn = i + 1;
                    else if (page <= 3) pn = i + 1;
                    else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                    else pn = page - 2 + i;
                    return (
                      <button key={pn} onClick={() => setPage(pn)}
                        className={`w-9 h-9 flex items-center justify-center rounded-xl font-bold text-xs transition ${page === pn ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}>{pn}</button>
                    );
                  })}
                </div>
                <button disabled={page === totalPages || loading} onClick={() => setPage(p => p + 1)}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition font-bold text-xs">
                  Next <ChevronRight size={16} />
                </button>
              </div>
              <div className="hidden md:block text-xs font-semibold text-slate-400">
                Page <span className="text-blue-600">{page}</span> of {totalPages}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
